import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/utils";
import { photoUrl } from "@/components/PhotoUpload";
import { RequestChat } from "@/components/RequestChat";
import { Mail, Phone, ChevronDown, ChevronUp, AlertCircle, Package, MapPin, Calendar, Trash2 } from "lucide-react";

function Offers({ requestId }) {
  const [offers, setOffers] = useState(null);
  useEffect(() => { api.get(`/admin/requests/${requestId}/offers`).then((r) => setOffers(r.data)).catch(() => setOffers([])); }, [requestId]);
  if (!offers) return <div className="text-xs text-slate-500">Angebote laden…</div>;
  if (!offers.length) return <div className="text-xs text-slate-500">Keine passenden Angebote generiert.</div>;
  return (
    <div className="space-y-1.5" data-testid={`admin-request-offers-${requestId}`}>
      {offers.map((o) => (
        <div key={o.id} className="flex flex-wrap justify-between gap-2 text-xs rounded-md bg-slate-900/60 px-3 py-2">
          <span>{o.partner_name} · {o.route_label}{o.via_segment ? ` (Teilstrecke ${o.segment})` : ""} · {fmtDate(o.abfahrt)}</span>
          <span className="mono">{o.match_score} % · {o.customer_price} € · <span className={o.status === "ausgewaehlt" ? "text-emerald-400" : "text-slate-400"}>{o.status}</span></span>
        </div>
      ))}
    </div>
  );
}

function Detail({ r }) {
  const b = r.booking;
  return (
    <div className="border-t border-slate-800 mt-3 pt-4 grid lg:grid-cols-2 gap-5" data-testid={`admin-request-detail-${r.id}`}>
      <div className="space-y-3 text-sm">
        <div>
          <div className="eb-label mb-1.5">Kunde</div>
          <div className="font-semibold" data-testid={`admin-request-contact-name-${r.id}`}>{r.contact_name || <span className="text-slate-500 font-normal">Kein Name hinterlegt</span>}</div>
          {r.contact_email ? (
            <a href={`mailto:${r.contact_email}?subject=${encodeURIComponent(`Ihre Anfrage ${r.route_label} – Easy Beiladung`)}`} className="flex items-center gap-1.5 text-[#FF5A1F] hover:underline mt-1" data-testid={`admin-request-mailto-${r.id}`}><Mail className="w-3.5 h-3.5" /> {r.contact_email}</a>
          ) : <div className="flex items-center gap-1.5 text-amber-400 mt-1" data-testid={`admin-request-noemail-${r.id}`}><AlertCircle className="w-3.5 h-3.5" /> Keine E-Mail hinterlegt (Altanfrage)</div>}
          {r.contact_phone && <a href={`tel:${r.contact_phone.replace(/\s/g, "")}`} className="flex items-center gap-1.5 text-[#FF5A1F] hover:underline mt-1" data-testid={`admin-request-tel-${r.id}`}><Phone className="w-3.5 h-3.5" /> {r.contact_phone}</a>}
          <div className="text-xs text-slate-500 mt-1">{r.customer_id ? "Registrierter Kunde" : "Gast-Anfrage"}</div>
        </div>
        <div>
          <div className="eb-label mb-1.5">Transport</div>
          <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#FF5A1F]" /> {r.start?.street ? `${r.start.street}, ` : ""}{r.start?.plz} {r.start?.ort} → {r.dest?.street ? `${r.dest.street}, ` : ""}{r.dest?.plz} {r.dest?.ort}{r.distance_km ? ` · ${r.distance_km} km` : ""}</div>
          <div className="flex items-center gap-1.5 mt-1"><Calendar className="w-3.5 h-3.5 text-[#FF5A1F]" /> {r.flexible ? "zeitlich flexibel" : `${fmtDate(r.date_from) || "–"} bis ${fmtDate(r.date_to) || "–"}`}</div>
          <div className="flex items-center gap-1.5 mt-1"><Package className="w-3.5 h-3.5 text-[#FF5A1F]" /> {r.volume_m3} m³ · {r.cargo_type}{r.items?.length ? ` · ${r.items.length} Positionen` : ""}</div>
          {(r.access_start || r.access_dest) && <div className="text-xs text-slate-400 mt-1">Zugang: {r.access_start || "–"} → {r.access_dest || "–"}{r.access_options?.length ? ` · ${r.access_options.join(", ")}` : ""}</div>}
          {r.services?.length > 0 && <div className="text-xs text-slate-400 mt-1">Zusatzleistungen: {r.services.join(", ")}</div>}
          {r.items?.length > 0 && <div className="text-xs text-slate-400 mt-1">{r.items.map((i) => `${i.qty || i.count || 1}× ${i.name}`).join(", ")}</div>}
        </div>
        {r.photos?.length > 0 && <div><div className="eb-label mb-1.5">Fotos</div><div className="flex gap-1.5 flex-wrap">{r.photos.map((id) => <a key={id} href={photoUrl(id)} target="_blank" rel="noreferrer"><img src={photoUrl(id)} alt="" className="w-14 h-14 rounded object-cover border border-slate-700" /></a>)}</div></div>}
        {b && <div className="text-xs rounded-md bg-emerald-500/10 text-emerald-300 px-3 py-2" data-testid={`admin-request-booking-${r.id}`}>Buchung {b.id.slice(0, 8).toUpperCase()} · {b.status} · Zahlung {b.payment_status} – weitere Kommunikation über den Buchungs-Chat</div>}
        <button onClick={async () => {
          if (window.prompt(`Anfrage ${r.route_label} von ${r.contact_name || "Unbekannt"} (${r.contact_email || "ohne E-Mail"}) inkl. ${r.offers_count} Angeboten und ${r.messages_count} Nachrichten endgültig löschen?\nZur Bestätigung LOESCHEN eingeben:`) !== "LOESCHEN") return;
          try { await api.delete(`/admin/requests/${r.id}`, { params: { confirm: "LOESCHEN" } }); toast.success("Anfrage gelöscht (Sicherung im Archiv)"); window.dispatchEvent(new Event("request-message-sent")); }
          catch (e) { toast.error(apiError(e.response?.data?.detail)); }
        }} className="eb-btn-ghost text-red-400 text-xs" data-testid={`admin-request-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /> Anfrage löschen</button>
      </div>
      <div>
        <div className="eb-label mb-1.5">Angebote ({r.offers_count})</div>
        <Offers requestId={r.id} />
        <RequestChat requestId={r.id} me="admin" defaultOpen={r.messages_count > 0 || r.unanswered} request={r} />
      </div>
    </div>
  );
}

export function AdminRequests({ useData, initialId }) {
  const { data, loading, reload } = useData("/admin/requests");
  const [openId, setOpenId] = useState(initialId || null);
  useEffect(() => {
    const onSent = () => reload();
    window.addEventListener("request-message-sent", onSent);
    return () => window.removeEventListener("request-message-sent", onSent);
  }, [reload]);
  if (loading) return <div className="text-slate-400">Lädt…</div>;
  return (
    <div className="space-y-3" data-testid="admin-requests">
      {data.length === 0 && <div className="eb-card p-8 text-center text-slate-500">Keine Anfragen</div>}
      {data.map((r) => {
        const open = openId === r.id;
        return (
          <div key={r.id} className="eb-card p-4" data-testid={`admin-request-row-${r.id}`}>
            <button onClick={() => setOpenId(open ? null : r.id)} className="w-full flex flex-wrap items-center justify-between gap-3 text-left" data-testid={`admin-request-toggle-${r.id}`}>
              <div>
                <div className="font-head font-semibold flex items-center gap-2 flex-wrap">{r.route_label}
                  <span className="eb-badge bg-slate-800 text-slate-300">{r.status}</span>
                  {r.booking && <span className="eb-badge bg-emerald-500/15 text-emerald-400">gebucht</span>}
                  {r.unanswered && <span className="eb-badge bg-[#FF5A1F]/20 text-[#FF5A1F]" data-testid={`admin-request-unanswered-${r.id}`}>Antwort ausstehend</span>}
                  {r.messages_count > 0 && <span className="eb-badge bg-sky-500/15 text-sky-300">{r.messages_count} Nachrichten</span>}
                  {!r.contact_email && <span className="eb-badge bg-amber-500/15 text-amber-400">ohne E-Mail</span>}
                </div>
                <div className="text-sm text-slate-400 mt-1">{fmtDate(r.created_at)} · {r.volume_m3} m³ · {r.cargo_type} · {r.offers_count} Angebote · <span className="text-slate-300">{r.contact_name || "Unbekannt"}</span>{r.contact_email ? ` · ${r.contact_email}` : ""}{r.contact_phone ? ` · ${r.contact_phone}` : ""}</div>
              </div>
              {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>
            {open && <Detail r={r} />}
          </div>
        );
      })}
    </div>
  );
}
